import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children }) => {
  // Close with Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0,
        width: '100vw',
        height: '100vh',
        margin: 0,
        padding: 0
      }}
    >
      {/* Blurred background - COVERS ENTIRE SCREEN */}
      <div 
        className="fixed inset-0 bg-black/80"
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backdropFilter: 'blur(8px)', 
          WebkitBackdropFilter: 'blur(8px)',
          zIndex: 1
        }}
        onClick={onClose}
      />
      
      {/* Modal content - PERFECTLY CENTERED */}
      <div 
        className="relative bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-3xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden animate-fade-in"
        style={{
          position: 'relative',
          zIndex: 10,
          margin: 'auto',
          transform: 'translate(0, 0)'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700/50">
          <h2 className="text-2xl font-bold text-white">
            {title}
          </h2>
          <button
            onClick={onClose}
            data-modal-close
            className="w-10 h-10 bg-slate-700/50 hover:bg-slate-600/50 rounded-xl flex items-center justify-center text-slate-400 hover:text-white transition-all duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Scrollable content */}
        <div className="p-6 overflow-y-auto max-h-[70vh] text-slate-200">
          {children}
        </div>
      </div>
    </div>
  );

  // CRUCIAL: Use createPortal to attach to body
  return createPortal(modalContent, document.body);
};

export default Modal;

